import React, { useState, useEffect } from "react";
import {
  AppBar,
  Badge,
  Box,
  Button,
  Collapse,
  Container,
  Stack,
  Toolbar,
  Typography,
  Link,
  Divider,
  IconButton,
  Drawer,
  List,
  ListItem,
  ListItemText,
  Tooltip,
} from "@mui/material";
import { motion } from "framer-motion";
import {
  Devices,
  Language,
  Wifi,
  Apple,
  Android,
  Microsoft,
  ExpandMore,
  ExpandLess,
  Link as LinkIcon,
  Newspaper,
  Logout,
} from "@mui/icons-material";
import { useNavigate } from "react-router-dom";

const vodafoneRed = "#e60000";
const darkRed = "#b00000";
const subButtonColor = "#f5f5f5";

// Dummy Changelog-Daten
const changelogData = [
  {
    version: "Beta 0.0.1",
    changes: [
      "Initiale Tool-Übersicht erstellt",
      "Buttons hinzugefügt",
      "Login-Funktion hinzugefügt",
    ],
  },
  {
    version: "Beta 0.0.2",
    changes: ["Changelog-Drawer eingebaut", "Badge für ungelesene Updates"],
  },
    {
    version: "Beta 0.0.3",
    changes: [
        "Geräte-Zugriff => Endgeräte Simulator", 
        "Aktuelle Betriebssysteme als Buttons hinzugefügt",
        "iOS => iPhone, macOS => MacBook"
    ],
  },
];

function AnimatedButton({ children, icon, isSub = false, ...props }) {
  return (
    <motion.div whileHover={{ scale: 1.03 }} whileTap={{ scale: 0.97 }} style={{ width: "100%" }}>
      <Button
        startIcon={icon}
        variant="contained"
        sx={{
          backgroundColor: isSub ? subButtonColor : vodafoneRed,
          color: isSub ? "#333" : "white",
          width: "100%",
          justifyContent: "flex-start",
          textTransform: "none",
          fontWeight: "bold",
          fontSize: "1rem",
          boxShadow: isSub ? "none" : undefined,
          "&:hover": {
            backgroundColor: isSub ? "#e0e0e0" : darkRed,
          },
        }}
        {...props}
      >
        {children}
      </Button>
    </motion.div>
  );
}

export default function VF_Tool() {
  const [openOSSim, setOpenOSSim] = useState(false);
  const [openFritz, setOpenFritz] = useState(false);
  const [openEasyBox, setOpenEasyBox] = useState(false);
  const [openAccess, setOpenAccess] = useState(false);
  const [openAndroid, setOpenAndroid] = useState(false);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [openIos, setOpenIos] = useState(false);
  const [openHuawei, setOpenHuawei] = useState(false);
  const [openSamsung, setOpenSamsung] = useState(false);
  const [openOneplus, setOpenOneplus] = useState(false);
  const [openXiaomi, setOpenXiaomi] = useState(false);

  const [unreadCount, setUnreadCount] = useState(2); // Simulierter Wert
  const navigate = useNavigate();

  const handleLogout = () => {
    localStorage.clear();
    navigate("/support-tools/login");
  };

  const markAsRead = () => {
    setUnreadCount(0);
  };

  return (
    <>
      <AppBar position="static" sx={{ backgroundColor: vodafoneRed }}>
        <Toolbar sx={{ justifyContent: "space-between" }}>
          <Typography variant="h6">Vodafone Support Tools</Typography>
          <Box>
            <Tooltip title="Changelog anzeigen">
              <IconButton color="inherit" onClick={() => setDrawerOpen(true)}>
                <Badge badgeContent={unreadCount} color="error">
                  <Newspaper />
                </Badge>
              </IconButton>
            </Tooltip>
            <Tooltip title="Abmelden">
              <IconButton color="inherit" onClick={handleLogout}>
                <Logout />
              </IconButton>
            </Tooltip>
          </Box>
        </Toolbar>
      </AppBar>

      <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ duration: 0.4 }}>
        <Container maxWidth="md" sx={{ mt: 4, mb: 6 }}>
          <Box textAlign="center" mb={4}>
            <Typography variant="h4" fontWeight="bold" gutterBottom>
              Admin-Bereich: Tools & Simulatoren
            </Typography>
            <Typography variant="body1" color="text.secondary">
              Wähle ein Tool oder einen Simulator
            </Typography>
          </Box>

          <Stack spacing={3}>
            {/* OS-Simulator */}
            <Box>
              <Typography variant="h6" sx={{ mb: 1 }}>
                OS-Simulatoren
              </Typography>
              <AnimatedButton onClick={() => setOpenOSSim(!openOSSim)} icon={<Devices />}>
                OS-Simulator {openOSSim ? <ExpandLess /> : <ExpandMore />}
              </AnimatedButton>
              <Collapse in={openOSSim} timeout="auto" unmountOnExit>
                <Stack spacing={1.5} sx={{ pl: 3, pt: 1 }}>
                  <AnimatedButton onClick={() => setOpenFritz(!openFritz)} isSub icon={<Wifi />}>
                    Fritz!Box
                  </AnimatedButton>
                  <Collapse in={openFritz} timeout="auto" unmountOnExit>
                    <Stack spacing={1} sx={{ pl: 3 }}>
                      <AnimatedButton onClick={() => alert("Einrichtungs-Simulator")} isSub icon={<Language />}>
                        Einrichtungs-Simulator
                      </AnimatedButton>
                      <AnimatedButton onClick={() => alert("Hauptseite-Simulator")} isSub icon={<Language />}>
                        Hauptseite-Simulator
                      </AnimatedButton>
                    </Stack>
                  </Collapse>

                  <AnimatedButton onClick={() => setOpenEasyBox(!openEasyBox)} isSub icon={<Wifi />}>
                    EasyBox
                  </AnimatedButton>
                  <Collapse in={openEasyBox} timeout="auto" unmountOnExit>
                    <Stack spacing={1} sx={{ pl: 3 }}>
                      <AnimatedButton onClick={() => alert("EasyBox Setup")} isSub icon={<Language />}>
                        Einrichtungs-Simulator
                      </AnimatedButton>
                      <AnimatedButton onClick={() => alert("EasyBox Hauptseite")} isSub icon={<Language />}>
                        Hauptseite-Simulator
                      </AnimatedButton>
                    </Stack>
                  </Collapse>
                </Stack>
              </Collapse>
            </Box>

            <Divider />

            {/* Gerätezugriff */}
            <Box>
            <Typography variant="h6" sx={{ mb: 1 }}>
                Endgerät-Simulatoren
            </Typography>

            <AnimatedButton onClick={() => setOpenAccess(!openAccess)} icon={<Devices />}>
                Endgerät-Simulator {openAccess ? <ExpandLess /> : <ExpandMore />}
            </AnimatedButton>

            <Collapse in={openAccess} timeout="auto" unmountOnExit>
                <Stack spacing={1.5} sx={{ pl: 3, pt: 1 }}>
                {/* macOS */}
                <AnimatedButton isSub icon={<Apple />}>
                    MacBook (macOS)
                </AnimatedButton>

                {/* iOS mit Unterversionen */}
                <AnimatedButton
                    isSub
                    onClick={() => setOpenIos(!openIos)}
                    icon={<Apple />}
                >
                    iPhone (iOS)
                </AnimatedButton>
                <Collapse in={openIos} timeout="auto" unmountOnExit>
                    <Stack spacing={1} sx={{ pl: 3 }}>
                    <AnimatedButton isSub>iOS 18</AnimatedButton>
                    <AnimatedButton isSub>iOS 19 (Beta)</AnimatedButton>
                    </Stack>
                </Collapse>

                {/* Windows */}
                <AnimatedButton isSub icon={<Microsoft />}>
                    Windows
                </AnimatedButton>

                {/* Android mit Marken */}
                <AnimatedButton
                    isSub
                    onClick={() => setOpenAndroid(!openAndroid)}
                    icon={<Android />}
                >
                    Android
                </AnimatedButton>
                <Collapse in={openAndroid} timeout="auto" unmountOnExit>
                    <Stack spacing={1} sx={{ pl: 3 }}>
                    {/* Huawei */}
                    <AnimatedButton
                        isSub
                        onClick={() => setOpenHuawei(!openHuawei)}
                    >
                        Huawei
                    </AnimatedButton>
                    <Collapse in={openHuawei} timeout="auto" unmountOnExit>
                        <Stack spacing={1} sx={{ pl: 3 }}>
                        <AnimatedButton isSub>HarmonyOS 4</AnimatedButton>
                        <AnimatedButton isSub>HarmonyOS Next (Beta)</AnimatedButton>
                        </Stack>
                    </Collapse>

                    {/* Samsung */}
                    <AnimatedButton
                        isSub
                        onClick={() => setOpenSamsung(!openSamsung)}
                    >
                        Samsung
                    </AnimatedButton>
                    <Collapse in={openSamsung} timeout="auto" unmountOnExit>
                        <Stack spacing={1} sx={{ pl: 3 }}>
                        <AnimatedButton isSub>One UI 6</AnimatedButton>
                        <AnimatedButton isSub>One UI 7 (Beta)</AnimatedButton>
                        </Stack>
                    </Collapse>

                    {/* OnePlus */}
                    <AnimatedButton
                        isSub
                        onClick={() => setOpenOneplus(!openOneplus)}
                    >
                        OnePlus
                    </AnimatedButton>
                    <Collapse in={openOneplus} timeout="auto" unmountOnExit>
                        <Stack spacing={1} sx={{ pl: 3 }}>
                        <AnimatedButton isSub>OxygenOS 14</AnimatedButton>
                        <AnimatedButton isSub>OxygenOS 15 (Preview)</AnimatedButton>
                        </Stack>
                    </Collapse>

                    {/* Xiaomi */}
                    <AnimatedButton
                        isSub
                        onClick={() => setOpenXiaomi(!openXiaomi)}
                    >
                        Xiaomi
                    </AnimatedButton>
                    <Collapse in={openXiaomi} timeout="auto" unmountOnExit>
                        <Stack spacing={1} sx={{ pl: 3 }}>
                        <AnimatedButton isSub>HyperOS</AnimatedButton>
                        <AnimatedButton isSub>MIUI 14</AnimatedButton>
                        </Stack>
                    </Collapse>
                    </Stack>
                </Collapse>
                </Stack>
            </Collapse>
            </Box>

            <Divider />

            {/* Weitere Links */}
            <Box>
              <Typography variant="h6" sx={{ mb: 1 }}>
                Weitere Tools & Links
              </Typography>
              <Stack spacing={1.5}>
                <AnimatedButton
                  component={Link}
                  href="https://www.router-faq.de/"
                  target="_blank"
                  rel="noopener"
                  icon={<LinkIcon />}
                >
                  Router-FAQ.de (Technische Daten)
                </AnimatedButton>
                <AnimatedButton
                  component={Link}
                  href="https://vodafone.de"
                  target="_blank"
                  rel="noopener"
                  icon={<LinkIcon />}
                >
                  Vodafone.de
                </AnimatedButton>
                <AnimatedButton disabled icon={<LinkIcon />}>
                  In Arbeit (temp)
                </AnimatedButton>
              </Stack>
            </Box>
          </Stack>
        </Container>

        {/* Footer */}
        <Box
          sx={{
            px: 2,
            py: 1,
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            backgroundColor: "#f0f0f0",
            borderTop: "1px solid #ddd",
          }}
        >
          <Typography variant="body2" color="text.secondary">
            © {new Date().getFullYear()} GermanMP.de
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Version 1.0.0
          </Typography>
        </Box>
      </motion.div>

      {/* Changelog Drawer */}
      <Drawer anchor="right" open={drawerOpen} onClose={() => setDrawerOpen(false)}>
        <Box sx={{ width: 360, p: 2 }}>
          <Typography variant="h6" gutterBottom>
            Changelog
          </Typography>
          <Button size="small" onClick={markAsRead} sx={{ mb: 2 }}>
            Als gelesen markieren
          </Button>
          <List sx={{ maxHeight: "70vh", overflowY: "auto" }}>
            {changelogData.map((log) => (
              <Box
                key={log.version}
                sx={{ mb: 2, p: 2, border: "1px solid #ddd", borderRadius: 2, backgroundColor: "#fafafa" }}
              >
                <Typography variant="subtitle1" fontWeight="bold">
                  Version {log.version}
                </Typography>
                <List disablePadding>
                  {log.changes.map((item, idx) => (
                    <ListItem key={idx} sx={{ pl: 0 }}>
                      <ListItemText primary={`• ${item}`} />
                    </ListItem>
                  ))}
                </List>
              </Box>
            ))}
          </List>
        </Box>
      </Drawer>
    </>
  );
}
